import type {
  SpaceRoles,
  UserRoles,
} from '@/services/firebaseServices/AuthServices/types';
import type { WebAppEnvironment } from '@/types/api';
import { currentConfigName } from '@/utils/firebase-config';
import {
  ALLOWED_EXTENSIONS_WITH_DOTS,
  ALLOWED_MIME_TYPES,
  DOCUMENT_ACCEPT_STRING as DOCUMENT_ACCEPT_STRING_SOURCE,
  DOCUMENT_ONLY_ACCEPT_STRING as DOCUMENT_ONLY_ACCEPT_STRING_SOURCE,
  DOCUMENT_ONLY_EXTENSIONS_WITH_DOTS,
  FILE_CATEGORY_SIZE_LIMITS_MB,
  getMaxFileSizeMB,
  MAX_DOCUMENT_SIZE_MB,
  MIME_TYPE_TO_CATEGORY,
} from '@/validations/document-file-types';

export const customTokenCookieName = 'customToken';
export const jwtCookieName = 'authToken';
export const targetEnvCookieName = 'targetEnv';
export const feedbackCookieName = 'feedbackFeatureActive';
export const hideUnmodifiedCourseWarningCookieName
  = 'hideUnmodifiedCourseWarning';
export const activeSpaceCookieName = 'lastUsedSpace';

export const MAX_VIDEO_DURATION = 5;
export const MAX_FILE_SIZE_MB = 25;

// Re-export document file size limits from single source of truth
export { FILE_CATEGORY_SIZE_LIMITS_MB as DOCUMENT_FILE_SIZE_LIMITS_MB };
export { MAX_DOCUMENT_SIZE_MB as MAX_DOCUMENT_FILE_SIZE_MB };

export const recorderPlaystoreUrl = 'https://play.google.com/store/apps/details?id=fm.how.recorder';
export const recorderAppstoreUrl = 'https://apps.apple.com/de/app/how-fm-recorder/id1533238782';

// Inline proxy logic for img.how.fm
export const SERVICE_ADDRESS = 'img.how.fm';
export const SERVICE_VERSION = '1';

// Media Gallery supported file types and formats
export const MEDIA_GALLERY_FILE_TYPES = {
  mimeTypes: [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/svg+xml',
    'video/mp4',
    'video/quicktime',
    'video/webm',
  ],
  extensions: ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.mp4', '.mov', '.webm'],
};

// Accept string for file input element
export const MEDIA_GALLERY_ACCEPT_STRING = 'image/jpeg, image/jpg, image/png, image/gif, image/svg+xml, video/mp4, video/quicktime, video/webm, .jpg, .jpeg, .png, .gif, .svg, .mp4, .mov, .webm';

// Document MIME types for autocourse - derived from single source of truth
export const DOCUMENT_FILE_TYPES = {
  mimeTypes: [...ALLOWED_MIME_TYPES],
  extensions: [...ALLOWED_EXTENSIONS_WITH_DOTS],
};

// Document-only file types (excluding video) for autocourse without video2course
export const DOCUMENT_ONLY_FILE_TYPES = {
  mimeTypes: ALLOWED_MIME_TYPES.filter(
    mimeType => MIME_TYPE_TO_CATEGORY[mimeType] !== 'video',
  ),
  extensions: [...DOCUMENT_ONLY_EXTENSIONS_WITH_DOTS],
};

// Accept string for document file input element
export const DOCUMENT_ACCEPT_STRING = DOCUMENT_ACCEPT_STRING_SOURCE;

// Accept string for document-only file input element (no video)
export const DOCUMENT_ONLY_ACCEPT_STRING = DOCUMENT_ONLY_ACCEPT_STRING_SOURCE;

// File validation constants
export const FILE_VALIDATION = {
  mediaGallery: {
    maxSizeMB: MAX_FILE_SIZE_MB,
    maxVideoDurationSeconds: MAX_VIDEO_DURATION,
    acceptString: MEDIA_GALLERY_ACCEPT_STRING,
    allowedTypes: [
      ...MEDIA_GALLERY_FILE_TYPES.mimeTypes,
      ...MEDIA_GALLERY_FILE_TYPES.extensions,
    ],
  },
  documents: {
    maxSizeMB: FILE_CATEGORY_SIZE_LIMITS_MB,
    acceptString: DOCUMENT_ACCEPT_STRING,
    allowedTypes: [
      ...DOCUMENT_FILE_TYPES.mimeTypes,
      ...DOCUMENT_FILE_TYPES.extensions,
    ],
    /**
     * Get max size for a specific file type
     * Uses the centralized getMaxFileSizeMB from document-file-types
     */
    getMaxSizeForType(mimeType: string | undefined, fileName: string): number {
      return getMaxFileSizeMB(mimeType, fileName);
    },
  },
  documentsOnly: {
    maxSizeMB: FILE_CATEGORY_SIZE_LIMITS_MB,
    acceptString: DOCUMENT_ONLY_ACCEPT_STRING,
    allowedTypes: [
      ...DOCUMENT_ONLY_FILE_TYPES.mimeTypes,
      ...DOCUMENT_ONLY_FILE_TYPES.extensions,
    ],
    getMaxSizeForType(mimeType: string | undefined, fileName: string): number {
      return getMaxFileSizeMB(mimeType, fileName);
    },
  },
};

/**
 * Utility function to check if a file type is allowed
 * @param file The file to check
 * @param allowedMimeTypes Array of allowed MIME types
 * @param allowedExtensions Array of allowed file extensions with dot prefix
 * @returns Boolean indicating if the file type is allowed
 */
export function isFileTypeAllowed(
  file: File,
  allowedMimeTypes: string[] = MEDIA_GALLERY_FILE_TYPES.mimeTypes,
  allowedExtensions: string[] = MEDIA_GALLERY_FILE_TYPES.extensions,
): boolean {
  if (file === null || file === undefined) {
    return false;
  }

  const fileType = file.type;
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

  const isAllowedMimeType = allowedMimeTypes.includes(fileType);
  const isAllowedExtension = allowedExtensions.includes(fileExtension);

  return isAllowedMimeType === true || isAllowedExtension === true;
}

/**
 * Utility function to check if a file size is within limits
 * @param file The file to check
 * @param maxSizeMB Maximum file size in MB
 * @param minSizeMB Minimum file size in MB
 * @returns Object with validation result and error message
 */
export function validateFileSize(
  file: File,
  maxSizeMB: number = MAX_FILE_SIZE_MB,
  minSizeMB: number = 0,
): { valid: boolean; error?: string } {
  const fileSizeInMB = file.size / (1024 * 1024);
  const formattedSize = fileSizeInMB.toFixed(2);

  if (fileSizeInMB < minSizeMB) {
    return {
      valid: false,
      error: `File size (${formattedSize}MB) is less than the minimum allowed (${minSizeMB}MB)`,
    };
  }

  if (fileSizeInMB > maxSizeMB) {
    return {
      valid: false,
      error: `File size (${formattedSize}MB) exceeds the maximum allowed (${maxSizeMB}MB)`,
    };
  }

  return { valid: true };
}

// this gtm id is the same as poc-cms. pending Martin's decision if a change is needed.
export const gtmID = 'GTM-T4Q3VVL';

// Data layer variables as per the GTM configuration screenshot
export function dataLayerVariables({
  target,
  hasInstructionFeedbackDisabled,
  user,
  version,
}: {
  target: WebAppEnvironment;
  hasInstructionFeedbackDisabled: boolean;
  user: {
    userId?: string;
    roleName?: UserRoles | SpaceRoles;
    space: string;
  };
  version: string;
}) {
  return {
    app_info: {
      version,
    },
    environment: {
      // Use the target passed from cookies/URL detection
      deployment_target: target,
    },
    user_properties: {
      target_environment: currentConfigName(target),
      user_disabled_instruction_feedback: hasInstructionFeedbackDisabled,
      ...user,
    },
  };
}

export const defaultDebounceMs = 300;
export const visiblePaginationPages = 8;

// ============================================================================
// PAGINATION - Single source of truth
// ============================================================================

export const MIN_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE = MIN_PAGE_SIZE * 2; // 20
export const PAGE_SIZE_OPTIONS = [MIN_PAGE_SIZE, DEFAULT_PAGE_SIZE, 30, 50];

// Sorting configuration
export const DEFAULT_SORT_FIELD = 'title';
export const DEFAULT_SORT_ORDER = 'asc' as const;

// ============================================================================
// API MAX PAGE SIZES - Based on backend route limits
// ============================================================================

// Editor API limit (sequences, quizzes, glossary, spaces, etc.)
// Source: api/src/shared/openapi/shared-schema.ts
export const MAX_PAGE_SIZE = 50;

// Extended API limit (profiles, AB tests, admin endpoints, course list)
// Source: api/src/routes/profile/profile.schema.ts, abtest.routes.ts, admin.routes.ts, course.routes.ts
export const MAX_EXTENDED_PAGE_SIZE = 9999;

export const IMAGE_PROCESSING_POLL_INTERVAL_MS = 5000; // 5 seconds between image processing status checks

/**
 * Utility function to generate the accept object required by react-dropzone
 * @param mimeTypes Array of MIME types to accept
 * @returns An object mapping MIME types to empty arrays as required by react-dropzone
 */
export function getDropzoneAcceptObject(
  mimeTypes: string[] = MEDIA_GALLERY_FILE_TYPES.mimeTypes,
): Record<string, string[]> {
  return mimeTypes.reduce<Record<string, string[]>>((acc, mimeType) => {
    return { ...acc, [mimeType]: [] };
  }, {});
}
